import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Service, OrderCreateRequest, TIME_SLOTS } from '../types';
import { Button, ServiceCard, ProgressIndicator, Input, Textarea, Select, ToggleButton } from '../components';
import { Card } from '../components/Card';
import { getFieldIcon } from '../utils/fieldIcons';
import { useConfig } from '../context/ConfigContext';
import { trackPageView, trackEvent } from '../utils/ga';

type BookingStep = 'service' | 'details' | 'date' | 'contact' | 'confirmation';

export const BookingPage: React.FC = () => {
  const { showPrices } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState<BookingStep>('service');
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(
    location.state?.selectedService || null
  );
  const [requiredInfo, setRequiredInfo] = useState<Record<string, any>>({});
  const [startDate, setStartDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', phone: '', address: '' });
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    trackPageView('/booking');
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await api.getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
    
    // If service was pre-selected, move to next step
    if (selectedService) {
      setCurrentStep('details');
    }
  }, [selectedService]);

  const steps = [
    { label: 'Service', completed: currentStep !== 'service', active: currentStep === 'service' },
    { label: 'Details', completed: ['date', 'contact', 'confirmation'].includes(currentStep), active: currentStep === 'details' },
    { label: 'Date', completed: ['contact', 'confirmation'].includes(currentStep), active: currentStep === 'date' },
    { label: 'Contact', completed: currentStep === 'confirmation', active: currentStep === 'contact' },
  ];

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setCurrentStep('details');
  };

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    // Validate required fields
    selectedService?.requiredInfoFields.forEach((field) => {
      if (field.required && !requiredInfo[field.name]) {
        newErrors[field.name] = `${field.name} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setCurrentStep('date');
  };

  const handleDateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    
    if (!startDate) {
      newErrors.startDate = 'Please select a date';
    }
    if (!timeSlot) {
      newErrors.timeSlot = 'Please select a time slot';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setCurrentStep('contact');
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!userInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!userInfo.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(userInfo.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!userInfo.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const orderData: OrderCreateRequest = {
        serviceId: selectedService!.serviceId,
        requiredInfo,
        userInfo,
        timeSlot,
        startDate,
        priceShown: selectedService!.price,
      };

      const result = await api.createOrder(orderData);
      
      // Track successful order submission
      trackEvent('order_submitted', {
        service_id: selectedService!.serviceId,
        service_name: selectedService!.serviceName,
        price: selectedService!.price,
        time_slot: timeSlot,
        start_date: startDate
      });
      
      setOrderResult(result);
      setCurrentStep('confirmation');
    } catch (error) {
      console.error('Error creating order:', error);
      setErrors({ submit: 'Failed to submit inquiry. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const resetBooking = () => {
    setCurrentStep('service');
    setSelectedService(null);
    setRequiredInfo({});
    setStartDate('');
    setTimeSlot('');
    setUserInfo({ name: '', phone: '', address: '' });
    setOrderResult(null);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl md:text-4xl font-bold text-center gradient-heading mb-8">
          Request a Service Inquiry
        </h1>

        {currentStep !== 'confirmation' && (
          <ProgressIndicator steps={steps} />
        )}

        {/* Step 1: Service Selection */}
        {currentStep === 'service' && (
          <div>
            <h2 className="text-2xl font-bold gradient-subheading mb-6 text-center">
              Choose Your Service
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service) => (
                <ServiceCard
                  key={service.serviceId}
                  service={service}
                  onClick={() => handleServiceSelect(service)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Service Details */}
        {currentStep === 'details' && selectedService && (
          <Card className="p-8">
            <h2 className="text-2xl font-bold gradient-subheading mb-6">
              Service Details
            </h2>
            <div className="mb-6 p-4 bg-primary-50 rounded-lg border-2 border-primary-500">
              <div className="flex items-center gap-2">
                {selectedService.serviceIcon && (
                  <img 
                    src={`https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/${selectedService.serviceIcon}`} 
                    alt={selectedService.serviceName}
                    className="w-6 h-6 object-contain"
                  />
                )}
                <h3 className="font-bold text-lg text-primary-800">{selectedService.serviceName}</h3>
              </div>
              {showPrices && <p className="text-primary-700">₹{selectedService.price}</p>}
            </div>
            <form onSubmit={handleDetailsSubmit} className="space-y-4">
              {selectedService.requiredInfoFields.map((field) => {
                const fieldIcon = getFieldIcon(field.name);
                return (
                  <div key={field.name}>
                    {field.dataType === 'boolean' ? (
                      <ToggleButton
                        value={requiredInfo[field.name] || false}
                        onChange={(value) =>
                          setRequiredInfo({ ...requiredInfo, [field.name]: value })
                        }
                        label={field.description || field.name}
                        required={field.required}
                        icon={fieldIcon}
                      />
                    ) : field.dataType === 'select' && field.options ? (
                      <Select
                        label={field.description || field.name}
                        value={requiredInfo[field.name] || ''}
                        onChange={(e) =>
                          setRequiredInfo({ ...requiredInfo, [field.name]: e.target.value })
                        }
                        options={field.options}
                        required={field.required}
                        error={errors[field.name]}
                        icon={fieldIcon}
                      />
                    ) : field.dataType === 'string' && field.name.toLowerCase().includes('instruction') ? (
                      <Textarea
                        label={field.description || field.name}
                        required={field.required}
                        value={requiredInfo[field.name] || ''}
                        onChange={(e) =>
                          setRequiredInfo({ ...requiredInfo, [field.name]: e.target.value })
                        }
                        error={errors[field.name]}
                        icon={fieldIcon}
                      />
                    ) : (
                      <Input
                        label={field.description || field.name}
                        type={field.dataType === 'number' ? 'number' : 'text'}
                        required={field.required}
                        value={requiredInfo[field.name] || ''}
                        onChange={(e) =>
                          setRequiredInfo({ ...requiredInfo, [field.name]: e.target.value })
                        }
                        error={errors[field.name]}
                        icon={fieldIcon}
                      />
                    )}
                  </div>
                );
              })}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('service')}
                >
                  Back
                </Button>
                <Button type="submit" className="flex-1">
                  Continue
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 3: Date and Time Selection */}
        {currentStep === 'date' && (
          <Card className="p-8">
            <h2 className="text-2xl font-bold gradient-subheading mb-6">
              Select Service Date & Time
            </h2>
            <form onSubmit={handleDateSubmit} className="space-y-4">
              <Input
                label="Preferred Start Date"
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                error={errors.startDate}
                min={new Date().toISOString().split('T')[0]}
                icon="https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/date-calendar.png"
              />
              
              <div className="w-full">
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Time Slot
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5">
                    <img src="https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/time-slot-clock.png" alt="" className="w-full h-full object-contain" />
                  </div>
                  <select
                    value={timeSlot}
                    onChange={(e) => setTimeSlot(e.target.value)}
                    className={`w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                      errors.timeSlot ? 'border-red-500' : ''
                    }`}
                    required
                  >
                    <option value="">Select a time slot</option>
                    {TIME_SLOTS.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.timeSlot && (
                  <p className="mt-1 text-sm text-red-500">{errors.timeSlot}</p>
                )}
              </div>
              
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('details')}
                >
                  Back
                </Button>
                <Button type="submit" className="flex-1">
                  Continue
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 4: Contact Information */}
        {currentStep === 'contact' && (
          <Card className="p-8">
            <h2 className="text-2xl font-bold gradient-subheading mb-6">
              Your Contact Information
            </h2>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <Input
                label="Full Name"
                type="text"
                required
                value={userInfo.name}
                onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                error={errors.name}
                placeholder="Enter your full name"
              />
              <Input
                label="Phone Number"
                type="tel"
                required
                value={userInfo.phone}
                onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                error={errors.phone}
                placeholder="Enter 10-digit phone number"
              />
              <Textarea
                label="Address"
                required
                value={userInfo.address}
                onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })}
                error={errors.address}
                placeholder="Enter the site address which needs cleaning"
              />
              {errors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('date')}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 5: Confirmation */}
        {currentStep === 'confirmation' && orderResult && (
          <Card className="p-8 text-center">
            {orderResult.success ? (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">✓</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Inquiry Submitted!
                </h2>
                <p className="text-gray-600 mb-6">
                  Thank you for your inquiry. We'll contact you shortly to discuss your cleaning needs and confirm the details.
                </p>
                <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                  <h3 className="font-bold text-lg mb-4">Inquiry Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service:</span>
                      <div className="flex items-center gap-2">
                        {selectedService?.serviceIcon && (
                          <img 
                            src={`https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/${selectedService.serviceIcon}`} 
                            alt={selectedService.serviceName}
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        <span className="font-medium">{selectedService?.serviceName}</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium">{startDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time Slot:</span>
                      <span className="font-medium">{timeSlot}</span>
                    </div>
                    {showPrices && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price:</span>
                        <span className="font-medium">₹{selectedService?.price}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate('/')}>
                    Back to Home
                  </Button>
                  <Button variant="outline" onClick={resetBooking}>
                    Submit Another Inquiry
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">✗</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Submission Failed
                </h2>
                <p className="text-gray-600 mb-6">
                  There was an error submitting your inquiry. Please try again.
                </p>
                <Button onClick={resetBooking}>
                  Try Again
                </Button>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};
